import { useFileStore } from '@/store/file.store'
import type {
  PortalCustomization,
  PortalCustomizationColors,
} from '@/types/nest/portal-customization'
import { axiosClient } from '@/utils/axios-client'
import type { UUID } from 'crypto'
import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'

export const usePortalCustomizationStore = defineStore('portalCustomizationStore', () => {
  const fileStore = useFileStore()

  /*
   * Fetch: {@link fetchPortalCustomization}
   */
  const portalCustomization: Ref<PortalCustomization | null> = ref(null)

  /*
   * The colors the portal currently shows. Equal to the saved colors, except while an admin previews
   * unsaved colors in the Branding tab. `Layout.vue` applies these to the page.
   */
  const activeColors: Ref<PortalCustomizationColors | null> = ref(null)

  /*
   * Object URL of the customer logo, or null when the LZP logo is used.
   * Fetch: {@link fetchLogo}
   */
  const logoUrl: Ref<string | null> = ref(null)

  /*
   * Data: {@link portalCustomization}
   */
  async function fetchPortalCustomization(customerId: UUID): Promise<void> {
    const response = await axiosClient.request<PortalCustomization>({
      url: `/customers/${customerId}/portal-customization`,
      method: 'GET',
    })

    portalCustomization.value = response.data
    resetActiveColors()
  }

  /*
   * Only the colors in `data` are changed; null resets a color to the default.
   * Data: {@link portalCustomization}
   */
  async function patchPortalCustomization(
    customerId: UUID,
    data: Partial<PortalCustomizationColors>
  ): Promise<void> {
    const response = await axiosClient.request<PortalCustomization>({
      url: `/customers/${customerId}/portal-customization`,
      method: 'PATCH',
      data,
    })

    portalCustomization.value = response.data
    resetActiveColors()
  }

  function previewColors(colors: PortalCustomizationColors): void {
    activeColors.value = { ...colors }
  }

  function resetActiveColors(): void {
    if (!portalCustomization.value) {
      activeColors.value = null
      return
    }

    const { customerId: _customerId, ...colors } = portalCustomization.value
    activeColors.value = colors
  }

  /**
   * The logo is the customer image (`Customer.imageFileId`), which is also used on delivery documents.
   * Data: {@link logoUrl}
   */
  async function fetchLogo(imageFileId: string | null | undefined): Promise<void> {
    const previousUrl = logoUrl.value

    logoUrl.value = imageFileId ? (await fileStore.downloadFile(imageFileId)).imageSrc : null

    if (previousUrl) URL.revokeObjectURL(previousUrl)
  }

  async function uploadLogo(customerId: UUID, file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)

    await axiosClient.request({
      url: `/customers/${customerId}/image`,
      method: 'PATCH',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    })
  }

  async function removeLogo(customerId: UUID): Promise<void> {
    await axiosClient.request({
      url: `/customers/${customerId}`,
      method: 'PATCH',
      data: { imageFileId: null },
    })
  }

  return {
    portalCustomization,
    activeColors,
    logoUrl,
    fetchPortalCustomization,
    patchPortalCustomization,
    previewColors,
    resetActiveColors,
    fetchLogo,
    uploadLogo,
    removeLogo,
  }
})
